import { useState, useEffect } from "react";
import { Loader2, Clock, Calendar, Plus, X, Save, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return localStorage.getItem("adminToken") || "";
}
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

export default function AdminAppointmentSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/appointment-settings`, { headers: headers() });
        setSettings(res.data);
      } catch { toast.error("Failed to load settings"); }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/appointment-settings`, settings, { headers: headers() });
      toast.success("Appointment settings saved");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const updateConfig = (type, field, value) => {
    setSettings(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value }
    }));
  };

  const addTime = (type) => {
    const times = [...(settings[type].specific_times || [])];
    times.push("12:00");
    updateConfig(type, "specific_times", times);
  };

  const removeTime = (type, idx) => {
    const times = [...(settings[type].specific_times || [])];
    times.splice(idx, 1);
    updateConfig(type, "specific_times", times);
  };

  const updateTime = (type, idx, val) => {
    const times = [...(settings[type].specific_times || [])];
    times[idx] = val;
    updateConfig(type, "specific_times", times);
  };

  const addBlockedDate = (type) => {
    const dates = [...(settings[type].blocked_dates || [])];
    dates.push(new Date().toISOString().slice(0, 10));
    updateConfig(type, "blocked_dates", dates);
  };

  const removeBlockedDate = (type, idx) => {
    const dates = [...(settings[type].blocked_dates || [])];
    dates.splice(idx, 1);
    updateConfig(type, "blocked_dates", dates);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
  );

  if (!settings) return <p className="text-center py-10 text-[#676664]/50">Unable to load settings</p>;

  const auspostOn = settings.auspost_content_visible ?? false;

  return (
    <AdminLayout
      title="Appointment Settings"
      subtitle="Configure pick-up and drop-off time slots"
      actions={
        <Button onClick={handleSave} disabled={saving} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-6" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}
        </Button>
      }
    >
      {/* Australia Post Content Toggle */}
      <div className="bg-white border border-[#282828]/8 p-6" data-testid="auspost-toggle-section">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base text-[#676664] flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <Package className="w-5 h-5 text-[#9A7B4F]" strokeWidth={1.5} />
              Australia Post Delivery Content
            </h3>
            <p className="text-xs text-[#676664]/50 mt-1">
              When enabled, shows prepaid return label info, Australia Post instructions across cart, checkout, PDP, booking confirmation emails, and borrowing process page.
              When disabled, all Australia Post references are hidden (for local delivery/pick-up mode).
            </p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, auspost_content_visible: !auspostOn }))}
            className={`relative w-12 h-6 rounded-full transition-colors ${auspostOn ? 'bg-[#9A7B4F]' : 'bg-[#282828]/15'}`}
            data-testid="auspost-toggle"
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${auspostOn ? 'left-[26px]' : 'left-0.5'}`} />
          </button>
        </div>
      </div>

      {["pickup", "dropoff"].map(type => {
        const config = settings[type] || {};
        const label = type === "pickup" ? "Pick-up" : "Drop-off";

        return (
          <div key={type} className="bg-white border border-[#282828]/8 p-6" data-testid={`settings-${type}`}>
            <h3 className="text-lg text-[#676664] mb-5 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <Calendar className="w-5 h-5 text-[#9A7B4F]" strokeWidth={1.5} />
              {label} Configuration
            </h3>

            {/* Mode Selection */}
            <div className="mb-5">
              <label className="text-xs uppercase tracking-wider text-[#676664]/50 block mb-2">Slot Mode</label>
              <div className="flex gap-2">
                {[{ key: "specific", label: "Specific Times" }, { key: "range", label: "Time Range" }, { key: "mixed", label: "Mixed" }].map(m => (
                  <button key={m.key} onClick={() => updateConfig(type, "mode", m.key)}
                    className={`px-4 py-2 text-xs border transition-colors ${config.mode === m.key ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]' : 'bg-white text-[#676664] border-[#282828]/12 hover:border-[#9A7B4F]/40'}`}
                    data-testid={`mode-${m.key}-${type}`}>{m.label}</button>
                ))}
              </div>
            </div>

            {/* Specific Times */}
            {(config.mode === "specific" || config.mode === "mixed") && (
              <div className="mb-5">
                <label className="text-xs uppercase tracking-wider text-[#676664]/50 block mb-2">Specific Time Slots</label>
                <div className="flex flex-wrap gap-2">
                  {(config.specific_times || []).map((t, i) => (
                    <div key={i} className="flex items-center gap-1 border border-[#282828]/12 bg-[#FAF8F0] pr-1">
                      <input type="time" value={t} onChange={e => updateTime(type, i, e.target.value)}
                        className="px-2 py-1.5 text-xs bg-transparent focus:outline-none w-24" data-testid={`time-input-${type}-${i}`} />
                      <button onClick={() => removeTime(type, i)} className="p-0.5 text-[#676664]/30 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => addTime(type)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-[#9A7B4F] border border-dashed border-[#9A7B4F]/30 hover:bg-[#9A7B4F]/5" data-testid={`add-time-${type}`}>
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>
              </div>
            )}

            {/* Range */}
            {(config.mode === "range" || config.mode === "mixed") && (
              <div className="mb-5 grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#676664]/50 block mb-1">Start Time</label>
                  <input type="time" value={config.range_start || "09:00"} onChange={e => updateConfig(type, "range_start", e.target.value)}
                    className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid={`range-start-${type}`} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#676664]/50 block mb-1">End Time</label>
                  <input type="time" value={config.range_end || "17:00"} onChange={e => updateConfig(type, "range_end", e.target.value)}
                    className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid={`range-end-${type}`} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#676664]/50 block mb-1">Interval (min)</label>
                  <input type="number" value={config.interval_minutes || 30} onChange={e => updateConfig(type, "interval_minutes", parseInt(e.target.value) || 30)}
                    min={15} step={15} className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid={`interval-${type}`} />
                </div>
              </div>
            )}

            {/* Max per slot */}
            <div className="mb-5">
              <label className="text-xs uppercase tracking-wider text-[#676664]/50 block mb-1">Max Bookings Per Slot</label>
              <input type="number" value={config.max_per_slot || 2} onChange={e => updateConfig(type, "max_per_slot", parseInt(e.target.value) || 1)}
                min={1} className="w-28 border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid={`max-per-slot-${type}`} />
            </div>

            {/* Day offset */}
            {type === "pickup" && (
              <div className="mb-5">
                <label className="text-xs uppercase tracking-wider text-[#676664]/50 block mb-1">Days Before Event</label>
                <input type="number" value={Math.abs(config.day_offset || 1)} onChange={e => updateConfig(type, "day_offset", -Math.abs(parseInt(e.target.value) || 1))}
                  min={1} max={3} className="w-28 border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="pickup-day-offset" />
                <p className="text-[10px] text-[#676664]/40 mt-1">Customer picks up this many days before their event</p>
              </div>
            )}
            {type === "dropoff" && (
              <div className="mb-5">
                <label className="text-xs uppercase tracking-wider text-[#676664]/50 block mb-1">Allowed Return Days After Event</label>
                <div className="flex gap-2">
                  {[1, 2, 3].map(d => {
                    const offsets = config.allowed_offsets || [1, 2];
                    const active = offsets.includes(d);
                    return (
                      <button key={d} onClick={() => {
                        const newOffsets = active ? offsets.filter(o => o !== d) : [...offsets, d].sort();
                        if (newOffsets.length > 0) updateConfig(type, "allowed_offsets", newOffsets);
                      }}
                        className={`px-4 py-2 text-xs border transition-colors ${active ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]' : 'bg-white text-[#676664] border-[#282828]/12'}`}
                        data-testid={`dropoff-offset-${d}`}>Day +{d}</button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#676664]/40 mt-1">Days after event when customer can return</p>
              </div>
            )}

            {/* Blocked Dates */}
            <div>
              <label className="text-xs uppercase tracking-wider text-[#676664]/50 block mb-2">Blocked Dates (No Appointments)</label>
              <div className="flex flex-wrap gap-2">
                {(config.blocked_dates || []).map((d, i) => (
                  <div key={i} className="flex items-center gap-1 border border-[#282828]/12 bg-red-50 pr-1">
                    <input type="date" value={d} onChange={e => {
                      const dates = [...config.blocked_dates];
                      dates[i] = e.target.value;
                      updateConfig(type, "blocked_dates", dates);
                    }} className="px-2 py-1.5 text-xs bg-transparent focus:outline-none" data-testid={`blocked-date-${type}-${i}`} />
                    <button onClick={() => removeBlockedDate(type, i)} className="p-0.5 text-red-400/50 hover:text-red-500"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <button onClick={() => addBlockedDate(type)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-500/60 border border-dashed border-red-300/40 hover:bg-red-50" data-testid={`add-blocked-${type}`}>
                  <Plus className="w-3 h-3" /> Block Date
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </AdminLayout>
  );
}
